import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../auth/apiFetch';
import { getToken } from '../auth/PasswordGate';

export interface CreditScoreEntry {
  id: number;
  score: number;
  bureau: string | null;
  model: string | null;
  /** ISO YYYY-MM-DD; observed (not necessarily entered) date. */
  observedAt: string;
  /** ISO timestamp; when the row landed in the DB. */
  createdAt: string;
}

export interface AddCreditScoreInput {
  score: number;
  bureau?: string | null;
  model?: string | null;
  /** ISO YYYY-MM-DD. Defaults to today server-side when omitted. */
  observed_at?: string;
}

export type Person = 'dakota' | 'natalia';

export interface CreditScoresData {
  dakota: CreditScoreEntry[];
  natalia: CreditScoreEntry[];
}

export type CreditScoresState =
  | { status: 'loading'; data: CreditScoresData }
  | {
      status: 'ready';
      data: CreditScoresData;
      reload: () => void;
      add: (input: AddCreditScoreInput) => Promise<void>;
      remove: (id: number) => Promise<void>;
    }
  | {
      status: 'error';
      data: CreditScoresData;
      error: string;
      reload: () => void;
    };

const EMPTY: CreditScoresData = { dakota: [], natalia: [] };

export function useCreditScores(): CreditScoresState {
  const [state, setState] = useState<CreditScoresState>({
    status: 'loading',
    data: EMPTY,
  });

  const load = useCallback(() => {
    const token = getToken();
    if (!token) {
      // Mirrors useRules: when locked, expose a no-op ready state so the
      // dashboard renders the empty card instead of a perpetual spinner.
      setState({
        status: 'ready',
        data: EMPTY,
        reload: load,
        add: async () => {},
        remove: async () => {},
      });
      return;
    }
    apiFetch('/api/credit/history')
      .then(async (res) => {
        if (!res.ok) throw new Error(`credit history ${res.status}`);
        return (await res.json()) as CreditScoresData;
      })
      .then((data) => {
        setState({
          status: 'ready',
          data: { dakota: data.dakota ?? [], natalia: data.natalia ?? [] },
          reload: load,
          add: async (input) => {
            const res = await apiFetch('/api/credit/score', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(input),
            });
            if (!res.ok) {
              const body = await res.text().catch(() => '');
              throw new Error(`add score ${res.status}: ${body.slice(0, 200)}`);
            }
            load();
          },
          remove: async (id) => {
            const res = await apiFetch(`/api/credit/score?id=${encodeURIComponent(id)}`, {
              method: 'DELETE',
            });
            if (!res.ok) {
              const body = await res.text().catch(() => '');
              throw new Error(`delete score ${res.status}: ${body.slice(0, 200)}`);
            }
            load();
          },
        });
      })
      .catch((err: Error) => {
        setState({
          status: 'error',
          data: EMPTY,
          error: err.message,
          reload: load,
        });
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return state;
}

/** Returns the most recent entry for a person, or null when they have none. */
export function latestFor(
  data: CreditScoresData,
  person: Person
): CreditScoreEntry | null {
  const list = data[person];
  return list.length > 0 ? list[0] : null;
}

/** Days between today and `observedAt` (UTC). Negative results clamp to 0.
 *  Accepts either a bare YYYY-MM-DD or a full ISO timestamp — Neon can
 *  surface DATE columns as either depending on driver behavior. */
export function daysSince(observedAt: string): number {
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(observedAt)
    ? `${observedAt}T00:00:00Z`
    : observedAt;
  const t = Date.parse(normalized);
  if (Number.isNaN(t)) return 0;
  const ms = Date.now() - t;
  return Math.max(0, Math.floor(ms / 86_400_000));
}
